import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, FileText, BookOpen, Presentation, File, ExternalLink } from 'lucide-react';
import { useToastNotifications } from '@/components/ui/toast-notification';

interface BusinessMaterial {
  id: number;
  title: string;
  description?: string;
  url: string;
  material_type: 'manual' | 'presentation' | 'document' | 'pdf';
  category?: string;
  icon_name: string;
  is_active: boolean;
  sort_order: number;
  file_size?: number;
  language: string;
  created_at: string;
  updated_at: string;
}

const MATERIAL_TYPES = [
  { value: 'manual', label: 'Manuál', icon: BookOpen },
  { value: 'presentation', label: 'Prezentace', icon: Presentation },
  { value: 'document', label: 'Dokument', icon: FileText },
  { value: 'pdf', label: 'PDF', icon: File }
];

const ICON_OPTIONS = [
  'FileText', 'BookOpen', 'Presentation', 'File', 'Download', 'ExternalLink', 
  'MonitorPlay', 'PlayCircle', 'Image', 'Archive'
];

export function BusinessMaterialsTab() {
  const { addNotification } = useToastNotifications();
  const queryClient = useQueryClient();
  const [editingMaterial, setEditingMaterial] = useState<BusinessMaterial | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    material_type: 'document',
    category: '',
    icon_name: 'FileText',
    is_active: true,
    sort_order: 0,
    file_size: '',
    language: 'cs'
  });

  // Fetch business materials
  const { data: materialsData, isLoading } = useQuery({
    queryKey: ['admin-business-materials'],
    queryFn: async () => {
      const response = await fetch('/api/admin/business-materials');
      if (!response.ok) throw new Error('Failed to fetch materials');
      return response.json();
    }
  });

  // Create material mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch('/api/admin/business-materials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create material');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-business-materials'] });
      setIsDialogOpen(false);
      resetForm();
      addNotification('success', 'Obchodní materiál byl úspěšně vytvořen');
    },
    onError: (error) => {
      addNotification('error', 'Chyba při vytváření materiálu: ' + error.message);
    }
  });

  // Update material mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await fetch(`/api/admin/business-materials/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update material');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-business-materials'] });
      setIsDialogOpen(false);
      setEditingMaterial(null);
      resetForm();
      addNotification('success', 'Obchodní materiál byl úspěšně aktualizován');
    },
    onError: (error) => {
      addNotification('error', 'Chyba při aktualizaci materiálu: ' + error.message);
    }
  });

  // Delete material mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/admin/business-materials/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete material');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-business-materials'] });
      addNotification('success', 'Obchodní materiál byl úspěšně smazán');
    },
    onError: (error) => {
      addNotification('error', 'Chyba při mazání materiálu: ' + error.message);
    }
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      url: '',
      material_type: 'document',
      category: '',
      icon_name: 'FileText',
      is_active: true,
      sort_order: 0,
      file_size: '',
      language: 'cs'
    });
  };

  const handleEdit = (material: BusinessMaterial) => {
    setEditingMaterial(material);
    setFormData({
      title: material.title,
      description: material.description || '',
      url: material.url,
      material_type: material.material_type,
      category: material.category || '',
      icon_name: material.icon_name,
      is_active: material.is_active,
      sort_order: material.sort_order,
      file_size: material.file_size?.toString() || '',
      language: material.language
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      file_size: formData.file_size ? parseInt(formData.file_size) : null
    };

    if (editingMaterial) {
      updateMutation.mutate({ id: editingMaterial.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = (id: number) => {
    if (confirm('Opravdu chcete smazat tento obchodní materiál?')) {
      deleteMutation.mutate(id);
    }
  };

  const openNewDialog = () => {
    setEditingMaterial(null);
    resetForm();
    setIsDialogOpen(true);
  };

  const getTypeIcon = (type: string) => {
    const typeData = MATERIAL_TYPES.find(t => t.value === type);
    const Icon = typeData?.icon || FileText;
    return <Icon className="h-4 w-4" />;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${Math.round(bytes / 1024)} KB` : `${mb.toFixed(1)} MB`;
  };

  const materials = materialsData?.data || [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Správa obchodních materiálů
              </CardTitle>
              <CardDescription>
                Spravujte prezentace, PDF dokumenty a obchodní manuály
              </CardDescription>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNewDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nový materiál
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingMaterial ? 'Upravit materiál' : 'Nový obchodní materiál'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingMaterial ? 'Upravte existující obchodní materiál' : 'Přidejte nový obchodní materiál'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Název *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Jazyk</Label>
                      <RadioGroup value={formData.language} onValueChange={(value: string) => setFormData({ ...formData, language: value })}>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="cs" id="cs" />
                          <Label htmlFor="cs">Čeština</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="en" id="en" />
                          <Label htmlFor="en">Angličtina</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Popis</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="url">URL *</Label>
                    <Input
                      id="url"
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://example.com/document.pdf"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Typ materiálu</Label>
                      <RadioGroup value={formData.material_type} onValueChange={(value: string) => setFormData({ ...formData, material_type: value })}>
                        {MATERIAL_TYPES.map(type => (
                          <div key={type.value} className="flex items-center space-x-2">
                            <RadioGroupItem value={type.value} id={type.value} />
                            <Label htmlFor={type.value} className="flex items-center gap-2">
                              <type.icon className="h-4 w-4" />
                              {type.label}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Kategorie</Label>
                      <Input
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        placeholder="např. training, sales, technical"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Ikona</Label>
                      <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto border rounded p-2">
                        {ICON_OPTIONS.map(icon => (
                          <div key={icon} className="flex items-center space-x-2">
                            <input
                              type="radio"
                              id={`icon-${icon}`}
                              name="icon_name"
                              value={icon}
                              checked={formData.icon_name === icon}
                              onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                              className="sr-only"
                            />
                            <Label 
                              htmlFor={`icon-${icon}`} 
                              className={`cursor-pointer p-1 rounded text-xs ${formData.icon_name === icon ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'}`}
                            >
                              {icon}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sort_order">Pořadí</Label>
                      <Input
                        id="sort_order"
                        type="number"
                        value={formData.sort_order}
                        onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="file_size">Velikost (bytes)</Label>
                      <Input
                        id="file_size"
                        type="number"
                        value={formData.file_size}
                        onChange={(e) => setFormData({ ...formData, file_size: e.target.value })}
                        placeholder="volitelné"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked: boolean) => setFormData({ ...formData, is_active: checked })}
                    />
                    <Label htmlFor="is_active">Aktivní</Label>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Zrušit
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      {editingMaterial ? 'Uložit změny' : 'Vytvořit materiál'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p>Načítám materiály...</p>
            </div>
          ) : materials.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Žádné materiály</p>
              <p className="text-sm">Přidejte první obchodní materiál</p>
            </div>
          ) : (
            <div className="space-y-4">
              {materials
                .sort((a: BusinessMaterial, b: BusinessMaterial) => a.sort_order - b.sort_order)
                .map((material: BusinessMaterial) => (
                <Card key={material.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        {getTypeIcon(material.material_type)}
                        <div className="flex-1">
                          <div className="font-medium">{material.title}</div>
                          {material.description && (
                            <div className="text-sm text-muted-foreground">
                              {material.description}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {MATERIAL_TYPES.find(t => t.value === material.material_type)?.label || material.material_type}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {material.language.toUpperCase()}
                            </Badge>
                            {material.category && (
                              <Badge variant="outline" className="text-xs">{material.category}</Badge>
                            )}
                            <Badge variant={material.is_active ? "default" : "secondary"} className="text-xs">
                              {material.is_active ? 'Aktivní' : 'Neaktivní'}
                            </Badge>
                            {material.file_size && (
                              <span className="text-xs text-muted-foreground">
                                {formatFileSize(material.file_size)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(material.url, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(material)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(material.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <FileText className="h-4 w-4" />
        <AlertDescription>
          <strong>Tip:</strong> Použite pořadí (sort_order) pro správné řazení materiálů. 
          Materiály typu "manual" se zobrazí s ikonou knihy, ostatní s příslušnými ikonami.
        </AlertDescription>
      </Alert>
    </div>
  );
}